// La fila de una notificación — ADR-2026-08-31-centro-de-notificaciones.
//
// Es la MISMA en el popover de escritorio y en la pantalla `/notificaciones`. Ese es
// todo el punto de que viva acá: dos listas de lo mismo que se ven distinto se
// desincronizan al primer retoque, y acá el retoque más probable (el tratamiento del
// no-leído) es justo el que tiene que ser idéntico en los dos lados.

import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../utils/imageCache';
import {
  presentacionNotificacion,
  tiempoRelativo,
  type Notificacion,
} from '../../lib/notificaciones';

/** Una hora, el mismo TTL que usan el historial y el preload de imágenes. */
const TTL_FIRMA = 3600;

/**
 * Miniatura de la imagen generada. El bucket `generadas` es PRIVADO, así que no
 * alcanza con una URL: hay que firmarla. El path lo arma el trigger
 * (`datos.file_path`) porque ahí ya está el `id_company`; el front sólo firma.
 *
 * Cae al casillero con ícono cuando no hay path (videos, errores, tipos que no son
 * generaciones) o cuando la firma falla. Nunca deja un hueco: el ícono es un estado
 * final válido, no un placeholder.
 */
function useUrlFirmada(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    path ? getCachedSignedUrl(path) : null,
  );

  useEffect(() => {
    if (!path) { setUrl(null); return; }
    const cacheada = getCachedSignedUrl(path);
    if (cacheada) { setUrl(cacheada); return; }

    let vigente = true;
    void (async () => {
      try {
        const { data } = await supabase.storage
          .from('generadas')
          .createSignedUrl(path, TTL_FIRMA);
        if (!vigente || !data?.signedUrl) return;
        setCachedSignedUrl(path, data.signedUrl);
        setUrl(data.signedUrl);
      } catch {
        // Se queda con el ícono. Una miniatura rota es peor que no tener miniatura.
      }
    })();
    return () => { vigente = false; };
  }, [path]);

  return url;
}

export interface FilaNotificacionProps {
  notificacion: Notificacion;
  /** Tap en la fila: el contenedor marca como leída y navega. */
  onAbrir: (n: Notificacion) => void;
  /**
   * `panel` = dentro del popover (más compacta). `pantalla` = la lista completa,
   * con algo más de aire porque ahí la fila es el contenido principal.
   */
  variante?: 'panel' | 'pantalla';
}

export function FilaNotificacion({
  notificacion,
  onAbrir,
  variante = 'panel',
}: FilaNotificacionProps) {
  const { icono, clases } = presentacionNotificacion(notificacion.tipo);
  const path = (notificacion.datos?.file_path as string | undefined) ?? null;
  const miniatura = useUrlFirmada(path);
  const esPantalla = variante === 'pantalla';

  return (
    <button
      type="button"
      onClick={() => onAbrir(notificacion)}
      className={`group flex w-full items-start gap-3 rounded-xl text-left transition-colors ${
        esPantalla ? 'px-3 py-3' : 'px-2.5 py-2.5'
      } ${
        // El no-leído se marca con un tinte de fondo Y con el punto de la derecha.
        // Sólo con el punto, en una lista donde la mayoría está sin leer, hay que
        // recorrer el borde derecho para saber qué es nuevo; sólo con el fondo, un
        // usuario que no distingue bien los tonos no lo ve. Los dos juntos es lo que
        // hacen Dovetail y Jira.
        notificacion.leida
          ? 'hover:bg-black/[0.03]'
          : 'bg-[rgb(var(--brand-primary-rgb)_/_0.045)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.075)]'
      }`}
    >
      {/* Casillero de 36px: o la miniatura real de lo que se generó, o el ícono del
          tipo. La foto gana siempre que exista — es literalmente lo que el usuario
          estaba esperando, y reconocerla de un vistazo vale más que cualquier ícono
          (el mismo criterio de la pantalla de referidos: en Atlas un crédito ES una
          foto, así que lo que se muestra son las fotos). */}
      {miniatura ? (
        <img
          src={miniatura}
          alt=""
          aria-hidden="true"
          className="h-9 w-9 shrink-0 rounded-[10px] object-cover ring-1 ring-black/[0.06]"
        />
      ) : (
        <span
          aria-hidden="true"
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-[10px] ${clases}`}
        >
          <span className="material-symbols-rounded text-[19px]">{icono}</span>
        </span>
      )}

      <span className="min-w-0 flex-1">
        {/* `font-[600]` y no `font-semibold`: el config del proyecto mapea
            `semibold` a 530, que en 13px se lee casi igual que el regular. */}
        <span className="block text-[13px] font-[600] leading-[18px] text-black/85">
          {notificacion.titulo}
        </span>
        {notificacion.cuerpo && (
          // Dos líneas como techo: la fila tiene que seguir siendo una fila. Lo que
          // no entra vive en la pantalla de destino, que es a donde lleva el tap.
          <span className="mt-0.5 line-clamp-2 block text-[12.5px] leading-[17px] text-black/50">
            {notificacion.cuerpo}
          </span>
        )}
        <span className="mt-1 block text-[11.5px] leading-none text-black/35">
          {tiempoRelativo(notificacion.created_at)}
        </span>
      </span>

      {/* El punto ocupa lugar SIEMPRE (`w-2` con o sin punto): sin eso, las filas
          leídas y las no leídas alinean su texto en dos columnas distintas y la
          lista se ve desprolija a medida que se van leyendo. */}
      <span className="mt-1.5 w-2 shrink-0" aria-hidden="true">
        {!notificacion.leida && (
          <span className="block h-2 w-2 rounded-full bg-brand-primary" />
        )}
      </span>
    </button>
  );
}

export default FilaNotificacion;
